import React from 'react';
import { siteData } from '../data/mock';
import { ArrowRight, Zap, Check } from 'lucide-react';

const HeroSection = () => {
  const heroMockup = "https://static.prod-images.emergentagent.com/jobs/c7d28edb-cb45-4bc7-8f27-ae38510fc8f7/images/c78404cc64689e14606e10cff44664770017c55b46d698dc2835aeeb3a4963ab.png";
  
  const highlights = [
    "5 ferramentas em 1 plataforma",
    "Automação completa do seu negócio",
    "Sem mensalidade no plano básico"
  ];

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900"
    >
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-purple-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 right-1/3 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
      </div>

      {/* Grid pattern overlay */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      <div className="container relative z-10 px-4 py-20 lg:py-28">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          {/* Left Content */}
          <div className="text-center lg:text-left">
            {/* Badge */}
            <div 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/20 border border-blue-400/30 mb-8 animate-fade-in-up backdrop-blur-sm"
            >
              <Zap size={16} className="text-blue-400" />
              <span className="text-sm font-medium text-blue-300">Automação para Profissionais</span>
            </div>

            {/* Title */}
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 animate-fade-in-up leading-tight"
              style={{ animationDelay: '0.1s' }}
              data-testid="hero-title"
            >
              {siteData.hero.title.split('. ').map((part, index) => (
                <span key={index}>
                  {index === 1 ? (
                    <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-purple-400 bg-clip-text text-transparent">{part}</span>
                  ) : (
                    <>{part}. <br className="hidden sm:block" /></>
                  )}
                </span>
              ))}
            </h1>

            {/* Subtitle */}
            <p
              className="text-lg text-slate-300 max-w-xl mx-auto lg:mx-0 mb-8 animate-fade-in-up"
              style={{ animationDelay: '0.2s' }}
            >
              {siteData.hero.subtitle}
            </p>

            {/* Highlights */}
            <div 
              className="flex flex-wrap justify-center lg:justify-start gap-4 mb-10 animate-fade-in-up"
              style={{ animationDelay: '0.25s' }}
            >
              {highlights.map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-slate-300">
                  <Check size={16} className="text-green-400" />
                  <span>{item}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start animate-fade-in-up"
              style={{ animationDelay: '0.3s' }}
            >
              <a
                href={siteData.whatsapp.getLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold rounded-xl shadow-lg shadow-blue-500/25 transition-all hover:scale-105 hover:shadow-xl hover:shadow-blue-500/30"
                data-testid="hero-cta-primary"
              >
                <Zap size={20} />
                {siteData.hero.cta}
                <ArrowRight size={20} />
              </a>
              <a
                href="#solucoes"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl border border-white/20 backdrop-blur-sm transition-all"
                data-testid="hero-cta-secondary"
              >
                Ver soluções
              </a>
            </div>
          </div>

          {/* Right - Mockup */}
          <div 
            className="relative animate-fade-in-up lg:pl-8"
            style={{ animationDelay: '0.4s' }}
          >
            {/* Glow effect behind mockup */}
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/30 to-purple-500/30 blur-3xl scale-90 opacity-50" />
            
            {/* Mockup image */}
            <div className="relative">
              <img
                src={heroMockup}
                alt="AtivaJá Dashboard - Ferramentas de automação para profissionais"
                className="w-full max-w-lg mx-auto drop-shadow-2xl"
                data-testid="hero-mockup"
              />
              
              {/* Floating badges */}
              <div className="absolute -top-4 -right-4 px-4 py-2 bg-green-500 text-white text-sm font-bold rounded-full shadow-lg animate-bounce">
                +500 usuários
              </div>
              <div className="absolute bottom-8 -left-4 px-4 py-2 bg-white text-slate-900 text-sm font-bold rounded-xl shadow-lg">
                ⭐ 4.9/5 avaliação
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce hidden lg:block">
        <div className="w-6 h-10 rounded-full border-2 border-white/30 flex justify-center pt-2">
          <div className="w-1.5 h-3 bg-white/50 rounded-full" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
